import { GrowthBook, GrowthBookProvider as GBProvider } from '@growthbook/growthbook-react';
import React, { ReactNode, useEffect, useMemo } from 'react';
import { useUserContext } from '../../contexts/UserContext';
import { getGrowthBookDeviceId } from './cookies';
import { createTrackingCallback } from './trackingCallback';

const GROWTHBOOK_API_HOST = 'https://cdn.growthbook.io';
const GROWTHBOOK_CLIENT_KEY = process.env.GROWTHBOOK_CLIENT_KEY || '';

interface Props {
  children: ReactNode;
}

/**
 * GrowthBook provider that integrates with the marketing site's device ID cookie.
 *
 * Key behaviors:
 * - Reads the existing gb_device_id cookie (does NOT create one)
 * - Uses device ID as the 'id' attribute for experiment assignment (matches marketing site)
 * - Tracks experiment exposures via RudderStack with "Experiment Viewed" events
 *
 * Important: We use 'id' = deviceId (not user.id) to ensure consistent experiment
 * bucketing between anonymous marketing site visitors and authenticated app users.
 */
export default function GrowthBookProvider({ children }: Props) {
  const { user, isLoggedIn } = useUserContext();

  const growthbook = useMemo(() => {
    const deviceId = getGrowthBookDeviceId();

    // If no device ID exists, GrowthBook will still work but experiments
    // won't be consistent with the marketing site
    if (!deviceId) {
      // eslint-disable-next-line no-console
      console.warn(
        '[GrowthBook] No gb_device_id cookie found. Experiments may not be consistent with marketing site.',
      );
    }

    return new GrowthBook({
      apiHost: GROWTHBOOK_API_HOST,
      clientKey: GROWTHBOOK_CLIENT_KEY,
      // Enable streaming for real-time feature updates
      enableDevMode: process.env.NODE_ENV === 'development',
      // Set initial attributes with device ID as 'id' to match marketing site
      attributes: {
        ...(deviceId ? { id: deviceId } : {}),
      },
      // Track experiment exposures via RudderStack
      trackingCallback: createTrackingCallback(),
    });
  }, []); // Only create once

  // Load features on mount
  useEffect(() => {
    growthbook.init({ streaming: true }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[GrowthBook] Failed to load features:', err);
    });

    return () => {
      growthbook.destroy();
    };
  }, [growthbook]);

  // Update attributes when user logs in/out
  // Note: We keep 'id' as deviceId for consistent bucketing - don't overwrite with user.id
  useEffect(() => {
    const deviceId = getGrowthBookDeviceId();

    growthbook.setAttributes({
      // Use 'id' = deviceId to match marketing site and ensure consistent bucketing
      ...(deviceId ? { id: deviceId } : {}),
      // Add user context for targeting (but NOT for bucketing)
      ...(isLoggedIn && user?.id ? { userId: String(user.id) } : {}),
      ...(isLoggedIn && user?.email ? { email: user.email } : {}),
    });
  }, [growthbook, isLoggedIn, user?.id, user?.email]);

  return <GBProvider growthbook={growthbook}>{children}</GBProvider>;
}
